/**
 * Ładunek i wymagania zlecenia w karcie „Zlecenie” — odczyt pól, które
 * API oddaje w szczegółach (`assignedVehicleType`, `ldm`, `keyPoints`).
 *
 * Reguły są lustrem backendu, żeby karta mówiła to samo, co podpowiadanie
 * aut: wymaganie wózka jak `requiresForklift` w
 * `user-offers/technical-fit.ts`, waga jak `orderWeightKg`
 * w `user-offers/load-capacity.ts`. Zgodność stałej pilnuje test.
 */
import { plNumber } from '../../shared/number';

/** Składnik wymaganego typu, który oznacza wózek (jak w API). */
export const FORKLIFT_REQUIREMENT = 'FORKLIFT';

/** Wymaganie rozbite na składniki — `+` łączy warunki, nie nazwy. */
export function requirementParts(requirement: unknown): string[] {
  if (typeof requirement !== 'string') return [];
  return requirement
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .split('+')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/**
 * Wymagany typ auta z dopiskiem o wózku.
 *
 * Sam `FORKLIFT` to czynność wózkowa (osobny rekord TMS, nie transport) —
 * nazywamy ją tak, a nie „wymaga wózka”. Brak typu — kreska, bez domysłów.
 */
export function vehicleTypeText(requirement: unknown): { type: string; forklift: string | null } {
  const parts = requirementParts(requirement);
  if (parts.length === 0) return { type: '—', forklift: null };
  const type = (requirement as string).trim();
  if (!parts.includes(FORKLIFT_REQUIREMENT)) return { type, forklift: null };
  return parts.length === 1
    ? { type, forklift: 'czynność wózkowa, nie transport' }
    : { type, forklift: 'wymaga wózka' };
}

const positive = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0;

/** LDM zamówione przez klienta, po polsku; brak — kreska. */
export function ldmText(ldm: unknown): string {
  return positive(ldm) ? `${plNumber(ldm, 2)} m` : '—';
}

/**
 * Waga ładunku: suma wag punktów załadunku. Zlecenie nie ma własnego pola
 * wagi — jest tylko `loadingWeight` punktu. Żaden punkt bez wagi — kreska
 * (nie zero).
 */
export function orderWeightText(points: unknown): string {
  if (!Array.isArray(points)) return '—';
  const weights = points
    .filter(
      (p) => !!p && typeof p === 'object' && (p as Record<string, unknown>).type === 'loading',
    )
    .map((p) => (p as Record<string, unknown>).loadingWeight)
    .filter(positive);
  if (weights.length === 0) return '—';
  return `${plNumber(
    weights.reduce((a, b) => a + b, 0),
    0,
  )} kg`;
}

/**
 * Waga na karcie wiersza banku: z punktów TMS, a gdy ich nie ma — waga
 * wpisana w prezlecenie (koperta wiersza z maila albo z formularza),
 * z dopiskiem, skąd jest. Wiersz prezlecenia nie ma punktów TMS, więc bez
 * tego karta pokazywała kreskę przy wpisanych 18 500 kg (porównanie
 * z makietą FTL, decyzja użytkownika 13.10.2026). Punkty TMS mają
 * pierwszeństwo — to dane z systemu klienta.
 */
export function bankRowWeightText(points: unknown, preOrderWeight: unknown): string {
  const fromPoints = orderWeightText(points);
  if (fromPoints !== '—' || !positive(preOrderWeight)) return fromPoints;
  return `${plNumber(preOrderWeight, 0)} kg (z prezlecenia)`;
}
